import firebase from 'firebase/app'
import 'firebase/firestore'
import { IGateMaster } from '@/models'

export class FirestoreService {
  private static db = firebase.firestore()

  public static getUserPermission(email: string) {
    return this.db
      .collection('administrators')
      .doc(email)
      .get()
  }

  public static getAdministratorMaster() {
    return this.db.collection('administrators').orderBy('email')
  }

  public static getCategoryMaster() {
    return this.db
      .collection('categoryMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getCompanyMaster() {
    return this.db
      .collection('companyMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getFacilityMaster() {
    return this.db
      .collection('facilityMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getGroupMaster() {
    return this.db
      .collection('groupMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getOptionMaster() {
    return this.db
      .collection('optionMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getPriceMaster() {
    return this.db.collection('priceMaster').orderBy('sortKey')
  }

  public static getTicketMaster() {
    return this.db
      .collection('ticketMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getSystemProperties() {
    return this.getSystemMaster().doc('properties')
  }

  /**
   * @description get api endpoint configuration for backend
   */
  public static getSystemEnvironment() {
    return this.getSystemMaster().doc('environment')
  }

  public static getSystemRouteSearch() {
    return this.getSystemMaster().doc('routeSearch')
  }

  public static getUserTransactions(saleID: string) {
    return this.db.collection('sales').doc(saleID)
  }

  public static getTicketsUnuse(userID: string) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'UNUSE')
      .orderBy('issueDateTime', 'desc')
  }

  public static getTicketsUsing(userID: string) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'USING')
      .orderBy('issueDateTime', 'desc')
  }

  public static getTicketsUsed(userID: string, displayHistoryLimit: number) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'USED')
      .orderBy('issueDateTime', 'desc')
      .limit(displayHistoryLimit)
  }

  public static getTicketFor(userID: string, ticketID: string) {
    return this.db.collection('users/' + userID + '/tickets').doc(ticketID)
  }

  public static getPackOrdersUnuse(userID: string) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'UNUSE')
      .where('isHide', '==', true)
      .orderBy('purchaseDateTime', 'desc')
  }

  public static getPackOrdersUsing(userID: string) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'USING')
      .where('isHide', '==', true)
      .orderBy('purchaseDateTime', 'desc')
  }

  public static getPackOrdersUsed(userID: string) {
    return this.db
      .collectionGroup('tickets')
      .where('userID', '==', userID)
      .where('status', '==', 'USED')
      .where('isHide', '==', true)
      .orderBy('purchaseDateTime', 'desc')
  }

  public static getOrders(userID: string) {
    return this.db.collectionGroup('orders').where('userID', '==', userID)
  }

  public static getOrder(userID: string, orderID: string) {
    return this.db.collection('user/' + userID + '/orders').doc(orderID)
  }

  private static getSystemMaster() {
    return this.db.collection('system')
  }

  public static getUserData(userID: string) {
    return this.db.collection('users').doc(userID)
  }

  public static getExternalLinkMaster() {
    return this.db
      .collection('externalLinkMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getExternalLowerLinkMaster() {
    return this.db
      .collection('externalLowerLinkMaster')
      .where('isDelete', '==', false)
      .orderBy('sortKey')
  }

  public static getTicketRegular() {
    return this.db
      .collection('ticketMaster')
      .where('isDelete', '==', 'false')
      .where('category', '==', 'NORMAL')
      .orderBy('sortKey')
  }

  public static getTicketCode() {
    return this.db
      .collection('ticketMaster')
      .where('isDelete', '==', 'false')
      .where('category', '==', 'CODE_TICKET')
      .orderBy('sortKey')
  }

  public static getTicketDocomo() {
    return this.db
      .collection('ticketMaster')
      .where('isDelete', '==', 'false')
      .where('category', '==', 'DOCOMO_BIKESHARE')
      .orderBy('sortKey')
  }

  public static getDocomoBikeSharePortMaster() {
    return this.db.collection('docomoBikeSharePortMaster')
  }

  public static async getGateInfo(gateID): Promise<IGateMaster | null> {
    try {
      const doc = await firebase
        .firestore()
        .collection('gateMaster')
        .doc(gateID)
        .get()
      if (doc.exists) {
        return doc.data() as IGateMaster
      }
      return null
    } catch (e) {
      return null
    }
  }

  public static getApplyTicketStatusAPPLYING() {
    return this.db
      .collection('applyTicketStatus')
      .where('applyStatus', '==', 'APPLYING')
      .orderBy('applyDateTime', 'desc')
  }

  public static getApplyTicketStatusEXAMINING() {
    return this.db
      .collection('applyTicketStatus')
      .where('applyStatus', '==', 'EXAMINING')
      .orderBy('applyDateTime', 'desc')
  }

  public static getApplyTicketStatusEXAMINED() {
    return this.db
      .collection('applyTicketStatus')
      .where('applyStatus', '==', 'EXAMINED')
      .orderBy('applyDateTime', 'desc')
  }

  public static getApplyTicketStatusEXAMINEDInRange(startDate: string, endDate: string) {
    return this.db
      .collection('applyTicketStatus')
      .where('applyStatus', '==', 'EXAMINED')
      .where('issueDateTime', '>=', startDate)
      .where('issueDateTime', '<=', endDate)
      .orderBy('applyDateTime', 'desc')
  }

  public static getApplyTicketStatusEXAMINEDAndSendMailStatus(
    startDate: string,
    endDate: string
  ) {
    return this.db
      .collection('applyTicketStatus')
      .where('applyStatus', '==', 'EXAMINED')
      .where('issueDateTime', '>=', startDate)
      .where('issueDateTime', '<=', endDate)
      .where('sentMailStatus', '==', 'FAIL')
      .orderBy('applyDateTime', 'desc')
  }

  public static getApplyTicketHistory() {
    return this.db.collection('applyTicketHistory').orderBy('downloadDateTime', 'desc')
  }

  public static getExaminationResultHistory() {
    return this.db.collection('examinationResultHistory').orderBy('uploadDateTime', 'desc')
  }

  public static getFacilityStaff() {
    return this.db.collection('facilityStaff').orderBy('email')
  }

  public static getMembers(userID: string) {
    return this.db.collection('members').where('userID', '==', userID)
  }

  public static getRegisterUsersHistory() {
    return this.db.collection('registerUsersHistory').orderBy('uploadDateTime', 'desc')
  }

  public static getRegisterUsersHistoryDetail(registerUsersID: string) {
    return this.db
      .collectionGroup('detail')
      .where('registerUsersID', '==', registerUsersID)
      .orderBy('displayNumber')
  }

  public static getRegisterUsersHistoryDetailWithSendMailStatus(registerUsersID: string) {
    return this.db
      .collectionGroup('detail')
      .where('registerUsersID', '==', registerUsersID)
      .where('sentMailStatus', '==', 'FAIL')
      .orderBy('displayNumber')
  }

  public static getSentMailHistory() {
    return this.db.collection('sentMailHistory').orderBy('sentDateTime', 'desc')
  }

  public static getUserApply(userID: string) {
    return this.db.collection('users/' + userID + '/apply').where('userID', '==', userID)
  }

  public static getUserOrders(userID: string) {
    return this.db.collectionGroup('orders').where('userID', '==', userID)
  }

  public static getUserOrder(userID: string, orderID: string) {
    return this.db.collection('user/' + userID + '/orders').doc(orderID)
  }
}
